import React from 'react';
import { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';

import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { FormWizardForm } from '@/shared/components/ui/form-wizard/components/form-wizard-form/form-wizard-form';
import { useFormWizard } from '@/shared/components/ui/form-wizard/context/form-wizard-context';
import { TFormWizardProps } from '@/shared/components/ui/form-wizard/form-wizard.types';

export const FormWizard = <TFormValues extends FieldValues>({
  forms,
  handleClose,
  onFormSubmit,
  overflowVisible,
  loaderText,
}: TFormWizardProps<TFormValues>) => {
  const {
    stepsData,
    setStepsData,
    setLastFormIndex,
    step,
    setStep,
    resetForm,
  } = useFormWizard<TFormValues>();
  const currentForm = forms[step];
  const { props, form, title, skipable, skipSteps } = currentForm;
  const {
    onSubmit,
    defaultValues,
    submitButtonLabel,
    resetDefaultValuesOnBack,
    formHasToBeDirty,
    ...rest
  } = props;

  const handleStepSubmit = async (
    data: TSubmittedData<TFormValues>,
    methods: UseFormReturn<TFormValues>,
  ) => {
    const newData = Object.assign([...stepsData], { [step]: data });
    setStepsData(newData);

    await onSubmit?.(data, methods);

    if (step < forms.length - 1) {
      setLastFormIndex(step);
      setStep(step + 1);
    } else {
      await onFormSubmit(newData);
      resetForm();
    }
  };

  const getDefaultValuesForStep = () => {
    const prevData = stepsData?.[step];

    if (prevData && !resetDefaultValuesOnBack) {
      return prevData.data as DefaultValues<TFormValues>;
    }

    if (defaultValues) {
      return defaultValues;
    }
  };

  return (
    <Form<TFormValues>
      key={step}
      defaultValues={getDefaultValuesForStep()}
      {...rest}
      onSubmit={handleStepSubmit}
      loaderText={loaderText}
    >
      {() => (
        <FormWizardForm
          maxSteps={forms.length}
          handleClose={handleClose}
          title={title}
          skipable={skipable}
          overflowVisible={overflowVisible}
          skipSteps={skipSteps}
          submitButtonLabel={submitButtonLabel}
          formHasToBeDirty={formHasToBeDirty}
        >
          {form}
        </FormWizardForm>
      )}
    </Form>
  );
};
